import React from 'react';
import { Box, Typography, Checkbox } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import WorkIcon from '@mui/icons-material/Work';
import { ContactInfo } from '../types';

interface ShareDraftContactCardProps {
  contact: ContactInfo;
  isSelected: boolean;
  onToggle: () => void;
}

export const ShareDraftContactCard: React.FC<ShareDraftContactCardProps> = ({
  contact,
  isSelected,
  onToggle,
}) => {
  // Determine if this is an owner based on the type or isOwner flag
  const isOwner =
    contact.isOwner || contact.type?.toLowerCase().includes('owner');

  return (
    <Box
      sx={{
        backgroundColor: 'grey.50',
        borderRadius: 1,
        p: 2,
        mb: 2,
        cursor: 'pointer',
        transition: 'all 0.2s',
        border: 1,
        borderColor: 'grey.200',
        '&:hover': {
          backgroundColor: 'grey.100',
          borderColor: 'primary.light',
        },
      }}
      onClick={onToggle}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        <Checkbox
          checked={isSelected}
          sx={{
            color: 'text.secondary',
            p: 0,
            '&.Mui-checked': { color: 'primary.main' },
          }}
        />

        <Box sx={{ flex: 1 }}>
          {/* Name */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <PersonIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 20 }} />
            <Typography
              sx={{ color: 'text.primary', fontWeight: isOwner ? 500 : 400 }}
            >
              {contact.name || 'Unknown'}
            </Typography>
          </Box>

          {/* Email */}
          {contact.email && (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <EmailIcon
                sx={{ color: 'text.secondary', mr: 1, fontSize: 20 }}
              />
              <Typography sx={{ color: 'text.primary', fontSize: '0.9rem' }}>
                {contact.email}
              </Typography>
            </Box>
          )}

          {/* Role/Type */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <WorkIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 20 }} />
            <Typography
              sx={{
                color: isOwner ? 'info.dark' : 'text.secondary',
                fontSize: '0.9rem',
                fontWeight: isOwner ? 500 : 400,
              }}
            >
              {contact.type}
            </Typography>
          </Box>

          {/* Team Info (if applicable) */}
          {contact.teamInfo && contact.teamInfo.teamName && (
            <Typography
              sx={{
                color: 'primary.main',
                opacity: 0.7,
                fontSize: '0.85rem',
                mt: 1,
              }}
            >
              Team: {contact.teamInfo.teamName}
            </Typography>
          )}

          {/* Owner OTP Notice */}
          {isOwner && (
            <Typography
              sx={{
                color: 'info.dark',
                fontSize: '0.8rem',
                mt: 1,
                fontStyle: 'italic',
              }}
            >
              Will require OTP verification
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};
